import React, { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  buildAppDeepLink,
  buildAppStoreUrl,
  buildPlayStoreUrl,
  detectPlatform,
} from '../utils/platform';
import './Download.css';

const Download = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  const redirected = useRef(false);

  const appStoreUrl = useMemo(() => buildAppStoreUrl(query), [query]);
  const playStoreUrl = useMemo(() => buildPlayStoreUrl(query), [query]);
  const deepLink = useMemo(() => buildAppDeepLink(query), [query]);

  useEffect(() => {
    if (redirected.current) return;
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem('kt-download-redirected') === '1') return;

    const platform = detectPlatform(
      window.navigator.userAgent,
      window.navigator.maxTouchPoints
    );
    if (platform === 'desktop') return;

    redirected.current = true;
    window.sessionStorage.setItem('kt-download-redirected', '1');
    window.location.replace(platform === 'android' ? playStoreUrl : appStoreUrl);
  }, [appStoreUrl, playStoreUrl]);

  return (
    <div className="download-page">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <Link to="/" className="logo">
              <img src="/assets/ic_kidstrail.png" alt="KidsTrail" className="logo-icon" />
              KidsTrail
            </Link>
          </div>
          <div className="header-center">
            <div className="header-download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="header-store-icon" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="header-store-icon" />
              </a>
            </div>
            <div className="header-social-buttons">
              <a
                href="https://www.youtube.com/@KidsTrailCare"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src="/assets/ic_youtube.png" alt="YouTube" className="header-social-icon" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585649347570"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/assets/ic_facebook.png" alt="Facebook" className="header-social-icon" />
              </a>
              <a
                href="https://www.instagram.com/kidstrail.care/"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/assets/ic_instagram.png" alt="Instagram" className="header-social-icon" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="download-section">
          <div className="container">
            <h1 className="download-title">Get KidsTrail</h1>
            <p className="download-subtitle">
              Free to download on iOS and Android. Pick your store below.
            </p>

            <div className="download-buttons">
              <a
                href={appStoreUrl}
                className="download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="store-icon" />
              </a>
              <a
                href={playStoreUrl}
                className="download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="store-icon" />
              </a>
            </div>

            {deepLink && (
              <a className="download-open-app" href={deepLink}>
                Already have the app? Open it
              </a>
            )}

            <Link to="/" className="download-home-btn">
              Back to Home
            </Link>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 KidsTrail. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/contact-us" className="footer-link">Contact Us</Link>
            <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Download;
